import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { MailOutlined, SaveOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useUnsavedChanges } from '../components/NavigationGuard';

const quiet = { silentError: true };
const defaults = { enabled: false, host: '', port: 587, secure: false, user: '', from: '', fromName: '轻锋机器人管理后台', password: '' };
const errorText = error => error.response?.data?.message || '操作失败，请检查网络后重试';

export default function MailSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [form] = Form.useForm(), [testForm] = Form.useForm();
  const enabled = Form.useWatch('enabled', form);
  const host = Form.useWatch('host', form), account = Form.useWatch('user', form);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    let active = true;
    setLoading(true); setError('');
    api.get('/settings/smtp', quiet).then(({ data }) => {
      if (!active) return;
      setSettings(data); form.setFieldsValue({ ...defaults, ...data, password: '' }); setDirty(false);
    }).catch(err => { if (active) setError(errorText(err)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [form, isAdmin, reload]);
  if (!isAdmin) return <Alert type="warning" showIcon message="邮件服务配置需要超级管理员权限" />;
  const accountChanged = settings?.hasPassword &&
    ((host || '').trim().toLowerCase() !== (settings.host || '').trim().toLowerCase() || (account || '').trim() !== (settings.user || '').trim());
  const disabled = loading || !settings || Boolean(busy);
  const save = async () => {
    try {
      const values = await form.validateFields();
      setBusy('save'); setError(''); setTestResult(null);
      const { data } = await api.put('/settings/smtp', values, quiet);
      setSettings(data); form.setFieldsValue({ ...data, password: '' }); setDirty(false);
      message.success('邮件服务配置已保存并立即生效');
    } catch (err) { if (!err.errorFields) setError(errorText(err)); }
    finally { setBusy(''); }
  };
  const sendTest = async () => {
    try {
      const values = await form.validateFields();
      const { recipient } = await testForm.validateFields();
      setBusy('test'); setError(''); setTestResult(null);
      const { data } = await api.post('/settings/smtp/test', { ...values, recipient }, quiet);
      setTestResult({ type: 'success', message: data.message });
    } catch (err) {
      if (!err.errorFields) setTestResult({ type: 'error', message: errorText(err) });
    } finally { setBusy(''); }
  };
  return <div className="mail-settings-page">
    <div className="page-header"><Typography.Title level={2}>邮件服务</Typography.Title>
      <div className="description">配置邮箱验证和密码找回使用的发信服务，保存后立即生效。</div></div>
    <Spin spinning={loading}>
      {error && <Alert className="page-alert" type="error" showIcon message={error} action={!settings && <Button onClick={() => setReload(value => value + 1)}>重试</Button>} />}
      {settings?.canStorePassword === false && <Alert className="page-alert" type="warning" showIcon message="服务器尚未设置授权码加密密钥"
        description="请先在服务器 .env 中设置至少 32 个字符的 ENCRYPTION_KEY 并重启后端，之后即可在此页面安全保存 SMTP 授权码。" />}
      <Card title={<Space><MailOutlined />SMTP 发信参数</Space>}>
        <Space wrap style={{ marginBottom: 20 }}>
          <Tag color={settings?.enabled ? 'green' : 'default'}>{settings?.enabled ? '已启用' : '已停用'}</Tag>
          {settings && <Tag>{settings.configurationSource === 'database' ? '使用后台保存的配置' : '沿用服务器环境配置'}</Tag>}
          {dirty && <Tag color="orange">有未保存的修改</Tag>}
        </Space>
        <Form form={form} name="smtp" layout="vertical" initialValues={defaults} disabled={disabled} onFinish={save}
          onValuesChange={() => { setDirty(true); setTestResult(null); }}>
          <Form.Item name="enabled" label="启用邮件服务" valuePropName="checked" extra="开启后可发送邮箱验证码和密码找回邮件；关闭后仍可使用当前密码修改密码。">
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Row gutter={24}>
            <Col xs={24} md={14}><Form.Item name="host" label="SMTP 服务器" rules={[{ required: Boolean(enabled), whitespace: true, message: '请输入 SMTP 服务器' }]}
              extra="填写服务商提供的主机名，例如 smtp.example.com。">
              <Input maxLength={253} placeholder="smtp.example.com" autoComplete="off" />
            </Form.Item></Col>
            <Col xs={24} md={10}><Form.Item name="port" label="SMTP 端口" rules={[{ required: true, message: '请输入 SMTP 端口' }]}>
              <InputNumber min={1} max={65535} precision={0} style={{ width: '100%' }} />
            </Form.Item></Col>
          </Row>
          <Form.Item name="secure" label="连接加密" getValueProps={value => ({ value: value ? 'tls' : 'starttls' })}
            getValueFromEvent={value => value === 'tls'} extra="始终使用加密连接并验证服务器证书；自定义端口请按服务商说明填写。">
            <Select onChange={mode => {
              const port = form.getFieldValue('port');
              if (!port || [465, 587].includes(port)) form.setFieldValue('port', mode === 'tls' ? 465 : 587);
            }} options={[{ value: 'starttls', label: 'STARTTLS（常用端口 587）' }, { value: 'tls', label: 'SSL/TLS（常用端口 465）' }]} />
          </Form.Item>
          <Row gutter={24}>
            <Col xs={24} md={12}><Form.Item name="user" label="SMTP 账号" rules={[{ required: Boolean(enabled), whitespace: true, message: '请输入 SMTP 账号' }]}>
              <Input maxLength={254} autoComplete="off" placeholder="通常为完整的邮箱地址" />
            </Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name="password" label="SMTP 授权码 / 密码" dependencies={['host', 'user', 'enabled']}
              rules={[{ required: Boolean((enabled || accountChanged) && (!settings?.hasPassword || accountChanged)), message: '请输入对应账号的 SMTP 授权码或密码' }]}
              extra={settings?.hasPassword ? '现有授权码不回显，留空保留；更换服务器或账号时需重新填写。' : '部分邮箱需先启用 SMTP，并使用服务商提供的授权码。'}>
              <Input.Password maxLength={2048} autoComplete="new-password" placeholder={settings?.hasPassword ? '已配置，留空保留现有授权码' : '请输入 SMTP 授权码或密码'} />
            </Form.Item></Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} md={12}><Form.Item name="from" label="发件邮箱" rules={[{ type: 'email', message: '请输入有效的发件邮箱' }]}
              extra="须为服务商授权的发件地址；留空时使用 SMTP 账号。">
              <Input type="email" maxLength={254} placeholder="sender@example.com" autoComplete="off" />
            </Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name="fromName" label="发件人名称">
              <Input maxLength={100} placeholder="轻锋机器人管理后台" />
            </Form.Item></Col>
          </Row>
          <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={busy === 'save'}>保存配置</Button>
        </Form>
      </Card>
      <Card title="发送测试邮件" className="section-card">
        <Typography.Paragraph type="secondary">使用上方当前填写的参数发送，不会保存或启用配置。收到测试邮件后，再保存配置供业务使用。</Typography.Paragraph>
        <Form form={testForm} name="smtp_test" layout="vertical" disabled={disabled} onFinish={sendTest} onValuesChange={() => setTestResult(null)}>
          <Form.Item name="recipient" label="测试收件邮箱" rules={[{ required: true, message: '请输入测试收件邮箱' }, { type: 'email', message: '请输入有效的测试收件邮箱' }]}>
            <Input type="email" maxLength={254} placeholder="请输入您用来接收测试邮件的邮箱" autoComplete="email" />
          </Form.Item>
          <Button icon={<MailOutlined />} htmlType="submit" loading={busy === 'test'}>发送测试邮件</Button>
        </Form>
        {testResult && <Alert style={{ marginTop: 20 }} showIcon {...testResult} />}
      </Card>
    </Spin>
  </div>;
}
